import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Fade,
  Grow,
  Slide,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, AccessTime, Note, Tag, CalendarMonth, List } from '@mui/icons-material';
import { format, isSameDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useAuth } from '../contexts/AuthContext';
import { getCareRecords, deleteCareRecord } from '../services/firestoreService';
import { toast } from 'react-toastify';
import { IconAvatar, InlineIcon } from './IconSystem';
import EditRecordForm from './EditRecordForm';

export default function RecordsList({ refreshTrigger }) {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [viewMode, setViewMode] = useState(0); // 0: list, 1: calendar
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filteredRecords, setFilteredRecords] = useState([]);

  const loadRecords = async () => {
    try {
      setLoading(true);
      const userRecords = await getCareRecords(currentUser.uid);
      setRecords(userRecords);
      setError('');
    } catch (error) {
      setError('記録の読み込みに失敗しました: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      loadRecords();
    }
  }, [currentUser, refreshTrigger]);

  useEffect(() => {
    if (viewMode === 1 && selectedDate) {
      const filtered = records.filter(record => 
        isSameDay(record.timestamp, selectedDate)
      );
      setFilteredRecords(filtered);
    } else {
      setFilteredRecords(records);
    }
  }, [records, selectedDate, viewMode]);

  const getRecordsForDate = (date) => {
    return records.filter(record => isSameDay(record.timestamp, date));
  };

  const getTileContent = ({ date }) => {
    const dayRecords = getRecordsForDate(date);
    if (dayRecords.length > 0) {
      return (
        <div style={{
          position: 'absolute',
          bottom: '2px',
          right: '2px',
          backgroundColor: '#66BB6A',
          borderRadius: '50%',
          width: '8px',
          height: '8px',
          fontSize: '8px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {dayRecords.length > 1 ? dayRecords.length : ''}
        </div>
      );
    }
    return null;
  };

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (record) => {
    setRecordToEdit(record);
    setEditDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCareRecord(recordToDelete.id);
      setRecords(records.filter(r => r.id !== recordToDelete.id));
      toast.success('記録を削除しました');
    } catch (error) {
      toast.error('削除に失敗しました: ' + error.message);
    }
    setDeleteDialogOpen(false);
    setRecordToDelete(null);
  };

  const handleRecordUpdated = () => {
    loadRecords(); // 記録を再読み込み
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) {
      return '時間未記録';
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}分`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}時間${remainingMinutes > 0 ? remainingMinutes + '分' : ''}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Fade in timeout={600}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center">
            <IconAvatar type="feeling" value="落ち着いた" size={48} />
            <Typography variant="h5" gutterBottom sx={{ ml: 2, mb: 0, fontWeight: 700 }}>
              過去の記録 ({viewMode === 1 && selectedDate ? filteredRecords.length : records.length}件)
            </Typography>
          </Box>
          
          <Tabs
            value={viewMode}
            onChange={(e, newValue) => setViewMode(newValue)}
            sx={{
              '& .MuiTab-root': {
                minWidth: 'auto',
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                mx: 0.5,
                fontWeight: 600,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #66BB6A, #42A5F5)',
                  color: 'white',
                },
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab icon={<List />} label="リスト" />
            <Tab icon={<CalendarMonth />} label="カレンダー" />
          </Tabs>
        </Box>
      </Fade>
      
      {viewMode === 1 ? (
        <Box>
          {/* カレンダービュー */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper 
                sx={{ 
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  '& .react-calendar': {
                    width: '100%',
                    border: 'none',
                    borderRadius: '16px',
                    backgroundColor: 'transparent',
                    fontFamily: 'inherit',
                  },
                  '& .react-calendar__tile': {
                    position: 'relative',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 187, 106, 0.2)',
                    },
                    '&--active': {
                      backgroundColor: '#66BB6A !important',
                      color: 'white !important',
                    },
                  },
                  '& .react-calendar__month-view__weekdays': {
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '0.75em',
                    color: '#666',
                  },
                  '& .react-calendar__navigation button': {
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 187, 106, 0.2)',
                    },
                  },
                }}
              >
                <Calendar
                  onChange={setSelectedDate}
                  value={selectedDate}
                  tileContent={getTileContent}
                  locale="ja-JP"
                />
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper 
                sx={{ 
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                  {format(selectedDate, 'yyyy年MM月dd日', { locale: ja })}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  この日の記録: {filteredRecords.length}件
                </Typography>
                
                {filteredRecords.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <IconAvatar type="feeling" value="空虚" size={48} />
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      この日は記録がありません
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {filteredRecords.map((record) => (
                      <Card 
                        key={record.id}
                        sx={{ 
                          mb: 2,
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <IconAvatar type="feeling" value={record.feeling} size={24} />
                            <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                              {record.timeUnknown ? '時刻不明' : format(record.timestamp, 'HH:mm')}
                            </Typography>
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                              <IconButton 
                                size="small"
                                onClick={() => handleEditClick(record)}
                                sx={{ 
                                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <EditIcon fontSize="small" sx={{ color: 'primary.main' }} />
                              </IconButton>
                            </Box>
                          </Box>
                          <Box display="flex" gap={0.5} mb={1} flexWrap="wrap">
                            <Chip 
                              label={record.feeling} 
                              size="small"
                              sx={{
                                background: 'linear-gradient(135deg, #66BB6A, #81C784)',
                                color: 'white',
                                fontSize: '0.7rem',
                              }}
                            />
                            <Chip 
                              label={record.content_type} 
                              size="small"
                              sx={{
                                background: 'linear-gradient(135deg, #42A5F5, #64B5F6)',
                                color: 'white',
                                fontSize: '0.7rem',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatDuration(record.duration)}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ) : (
        // リストビュー（既存のコード）
        <>  
          {records.length === 0 ? (
            <Fade in timeout={800}>
              <Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <IconAvatar type="feeling" value="空虚" size={64} />
                <Typography color="text.secondary" sx={{ mt: 2, fontSize: '1.1rem' }}>
                  まだ記録がありません。上のフォームから記録を追加してみましょう。
                </Typography>
              </Paper>
            </Fade>
          ) : (
            <Grid container spacing={3}>
              {records.map((record, index) => (
                <Grid item xs={12} key={record.id}>
                  <Grow in timeout={600 + index * 100}>
                    <Card 
                      sx={{ 
                        position: 'relative',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-4px) scale(1.02)',
                          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" mb={2}>
                              <IconAvatar type="feeling" value={record.feeling} size={40} />
                              <Box ml={2}>
                                <Typography variant="h6" fontWeight={600}>
                                  {record.timeUnknown 
                                    ? format(record.timestamp, 'yyyy年MM月dd日 (時刻不明)', { locale: ja })
                                    : format(record.timestamp, 'yyyy年MM月dd日 HH:mm', { locale: ja })
                                  }
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                  <AccessTime fontSize="small" sx={{ color: 'primary.main' }} />
                                  <Typography variant="body2" color="primary.main" fontWeight={500}>
                                    {formatDuration(record.duration)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
                              <Chip 
                                icon={<InlineIcon type="feeling" value={record.feeling} />}
                                label={record.feeling} 
                                sx={{
                                  background: 'linear-gradient(135deg, #66BB6A, #81C784)',
                                  color: 'white',
                                  fontWeight: 600,
                                  '&:hover': { transform: 'scale(1.05)' },
                                }}
                              />
                              <Chip 
                                icon={<InlineIcon type="content" value={record.content_type} />}
                                label={record.content_type} 
                                sx={{
                                  background: 'linear-gradient(135deg, #42A5F5, #64B5F6)',
                                  color: 'white',
                                  fontWeight: 600,
                                  '&:hover': { transform: 'scale(1.05)' },
                                }}
                              />
                              {record.tags.map((tag, tagIndex) => (
                                <Chip 
                                  key={tagIndex}
                                  icon={<Tag sx={{ fontSize: '0.875rem' }} />}
                                  label={tag} 
                                  variant="outlined"
                                  sx={{
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    fontWeight: 500,
                                    '&:hover': { 
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                      transform: 'scale(1.05)',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                />
                              ))}
                            </Box>

                            {record.note && (
                              <Box display="flex" alignItems="flex-start" mt={2}>
                                <Note sx={{ mr: 1, mt: 0.5, color: 'text.secondary', fontSize: '1.2rem' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                  {record.note}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                            <IconButton 
                              color="primary"
                              onClick={() => handleEditClick(record)}
                              sx={{ 
                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                '&:hover': {
                                  backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              color="error"
                              onClick={() => handleDeleteClick(record)}
                              sx={{ 
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '20px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>
          記録を削除
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
            この記録を削除してもよろしいですか？この操作は元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            キャンセル
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f44336, #e53935)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d32f2f, #c62828)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <EditRecordForm
        record={recordToEdit}
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setRecordToEdit(null);
        }}
        onRecordUpdated={handleRecordUpdated}
      />
    </Box>
  );
}